import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * Modal. Lo minimo que hace falta para que no se sienta barato:
 *
 *  · bloquea el scroll del fondo, o la pagina de detras se mueve al hacer rueda dentro
 *  · Escape cierra, y el foco entra al abrir y VUELVE a donde estaba al cerrar
 *  · el foco se queda atrapado dentro: con Tab no se puede salir a los botones de atras, que es
 *    lo que hace que un modal se sienta roto con teclado
 *  · va en un portal, para que ningun `overflow` de un padre lo recorte
 */
export function Modal({
  abierto, cerrar, titulo, sub, children, pie, ancho = 720, icono,
}: {
  abierto: boolean;
  cerrar: () => void;
  titulo: string;
  sub?: ReactNode;
  children: ReactNode;
  pie?: ReactNode;
  ancho?: number;
  /** Badge de icono opcional a la izquierda del título (ver `.mod-ico`). */
  icono?: ReactNode;
}) {
  const caja = useRef<HTMLDivElement>(null);
  const antes = useRef<Element | null>(null);

  useEffect(() => {
    if (!abierto) return;
    antes.current = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === "Escape") { cerrar(); return; }
      if (e.key !== "Tab" || !caja.current) return;
      const focos = caja.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!focos.length) return;
      const primero = focos[0]!, ultimo = focos[focos.length - 1]!;
      if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
    };

    document.addEventListener("keydown", alPulsar);
    // Orden a proposito: primero lo que el paso marque, luego el primer campo, y solo si no hay
    // nada de eso un boton. Con un selector unico ganaba la X de cerrar por estar antes en el DOM,
    // y el modal abria con el foco puesto en «salir de aqui».
    const foco =
      caja.current?.querySelector<HTMLElement>("[data-foco]") ??
      caja.current?.querySelector<HTMLElement>("input:not([disabled]), select:not([disabled])") ??
      caja.current?.querySelector<HTMLElement>("button:not([disabled])");
    foco?.focus();

    return () => {
      document.removeEventListener("keydown", alPulsar);
      document.body.style.overflow = overflow;
      (antes.current as HTMLElement | null)?.focus?.();
    };
  }, [abierto, cerrar]);

  if (!abierto) return null;

  return createPortal(
    <div className="telon" onMouseDown={(e) => e.target === e.currentTarget && cerrar()}>
      <div className="modal-caja" ref={caja} role="dialog" aria-modal aria-label={titulo}
        style={{ maxWidth: ancho }}>
        <header className="modal-cab">
          {icono ? (
            <div className="mod-titulo">
              <span className="mod-ico" aria-hidden>{icono}</span>
              <div>
                <h2>{titulo}</h2>
                {sub && <p className="detalle">{sub}</p>}
              </div>
            </div>
          ) : (
            <div>
              <h2>{titulo}</h2>
              {sub && <p className="detalle">{sub}</p>}
            </div>
          )}
          <button className="cerrar" onClick={cerrar} aria-label="Cerrar">✕</button>
        </header>
        <div className="modal-cuerpo">{children}</div>
        {pie && <footer className="modal-pie">{pie}</footer>}
      </div>
    </div>,
    document.body
  );
}

/** Barra de pasos. Dice donde estas y cuanto queda, que es la mitad de la confianza. */
export function Pasos({ pasos, actual }: { pasos: string[]; actual: number }) {
  return (
    <ol className="migas">
      {pasos.map((p, i) => (
        <li key={p} className={i === actual ? "aqui" : i < actual ? "hecho" : ""}>
          <span className="n">{i < actual ? "✓" : i + 1}</span>
          <span className="t">{p}</span>
        </li>
      ))}
    </ol>
  );
}

/** Bloque gris con forma de lo que va a venir. Dice "esto esta llegando", no "esto esta vacio". */
export function Hueco({ alto = 64, n = 1 }: { alto?: number; n?: number }) {
  return (
    <>
      {Array.from({ length: n }, (_, i) => (
        <div key={i} className="hueco" style={{ height: alto }} aria-hidden />
      ))}
    </>
  );
}

export function Giro({ texto }: { texto?: string }) {
  return (
    <span className="giro-cont">
      <span className="giro" aria-hidden />
      {texto && <span>{texto}</span>}
    </span>
  );
}
